/**
 * One audit row, assembled.
 *
 * Pulled out of `ui/auditor.ts` so a scheduled function can write the same shape of row the
 * screens write. The screens still go through `note()`, which calls this and fires the write;
 * what lives here is only the row — the id, the upper-casing, the dropped blanks — because a log
 * whose rows differ by who wrote them is a log with two formats.
 */

import type { AuditEntry } from '../store/types.js';

export interface Note {
  /**
   * The instant to stamp it with, when the caller has a better one than "now".
   *
   * A keeper diff is written a beat after the save it describes; stamping it with the
   * declaration's own `updatedAt` puts the two on the same instant, which is what lets
   * `mergeEvents` recognise them as one act rather than printing it twice.
   */
  at?: string;
  kind: AuditEntry['kind'];
  what: string;
  target?: string;
  onBehalfOf?: string;
  detail?: Record<string, string | undefined>;
  span?: 'start' | 'stop';
}

/**
 * Ids carry the minute and a random tail. The minute makes the log sortable by id when two rows
 * share a timestamp; the tail keeps two tabs of the same account from colliding, which would be a
 * silent loss on a collection that refuses updates.
 */
export function mintId(kind: string, actor: string, at = new Date().toISOString()): string {
  const stamp = at.replace(/[^0-9]/g, '').slice(0, 14);
  const tail = Math.random().toString(36).slice(2, 8);
  return `${stamp}-${kind}-${actor.toLowerCase()}-${tail}`;
}

/** Undefined values dropped: Firestore refuses them, and a blank field is not a fact. */
function clean(d: Record<string, string | undefined>): Record<string, string> {
  return Object.fromEntries(
    Object.entries(d).filter((e): e is [string, string] => typeof e[1] === 'string' && e[1] !== ''),
  );
}

export function auditEntry(season: number, actor: string, n: Note): AuditEntry {
  const at = n.at ?? new Date().toISOString();
  return {
    id: mintId(n.kind, actor, at),
    season,
    at,
    actor: actor.toUpperCase(),
    kind: n.kind,
    what: n.what,
    ...(n.target ? { target: n.target } : {}),
    ...(n.onBehalfOf ? { onBehalfOf: n.onBehalfOf.toUpperCase() } : {}),
    ...(n.span ? { span: n.span } : {}),
    ...(n.detail ? { detail: clean(n.detail) } : {}),
  };
}
